import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch, useFieldArray } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

const HOTOForm = ({ role = cookie.get('user-role') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentSignature, setCurrentSignature] = useState({ role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues, watch } = useForm({
    defaultValues: {
      site_name: '',
      location_address: '',
      station_id: stationId || '',
      chargers: [{ name: '' }, { name: '' }],
      charger_configuration: '',
      internal_connection: '',
      new_evcs_connection: '',
      sanctioned_load: '',
      date_of_commissioning: '',
      google_coordinates: { latitude: '', longitude: '' },
      project_punch_point: 'NILL',
      annexure_1: [
        { item_description: 'Documents Path', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Charger - 1 (OCPP ID)', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Charger - 2 (OCPP ID)', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'DB Panel - 1', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'DB Panel - 2', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Compact Sub Station KVA', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'EZVIZ CCTV Camera-1', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'EZVIZ CCTV Camera-2', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'CCTV Password', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'WIFI connection', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'SIM : Charger - 1', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'SIM : Charger - 2', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Canopy & Flooring', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Electrical Contractor', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Electric Connection', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
      ],
      acknowledgment: 'Acknowledge and accept the handover of Electric Vehicle Charging Station (EVCS).',
      comments: {
        project: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        maintenance: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        operations: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      other_comments: '',
      status: 'draft', // single-level workflow status
    },
  });

  const { fields: chargerFields, append: appendCharger, remove: removeCharger } = useFieldArray({
    control,
    name: 'chargers',
  });

  const formValues = useWatch({ control });
  const new_evcs_connection = watch('new_evcs_connection');

  // ---------- Autosave (does NOT alter status) ----------
  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const payload = {
        form_data: {
          status: data.status,
          form_id: formId,
          formName: 'hoto',
          stationId: data.station_id,
          site_name: data.site_name,
          location_address: data.location_address,
          chargers: data.chargers.map((c) => c.name),
          charger_configuration: data.charger_configuration,
          internal_connection: data.internal_connection,
          new_evcs_connection: data.new_evcs_connection,
          sanctioned_load: data.new_evcs_connection === 'Yes' ? data.sanctioned_load : '',
          date_of_commissioning: data.date_of_commissioning,
          google_coordinates: data.google_coordinates,
          project_punch_point: data.project_punch_point,
          annexure_1: data.annexure_1,
          acknowledgment: data.acknowledgment,
          comments: data.comments,
          other_comments: data.other_comments,
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => { autoSave(formValues); }, [formValues, autoSave]);

  // ---------- Load existing ----------
  useEffect(() => {
    if (formId) dispatch({ type: VIEW_FORM, payload: { formId, formName: 'hoto' } });
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const d = viewFormData || {};
      setValue('site_name', d.site_name || '');
      setValue('location_address', d.location_address || '');
      setValue('station_id', d.stationId || stationId || '');
      setValue('chargers', Array.isArray(d.chargers) ? d.chargers.map((name) => ({ name })) : [{ name: '' }, { name: '' }]);
      setValue('charger_configuration', d.charger_configuration || '');
      setValue('internal_connection', d.internal_connection || '');
      setValue('new_evcs_connection', d.new_evcs_connection || '');
      setValue('sanctioned_load', d.sanctioned_load || '');
      setValue('date_of_commissioning', d.date_of_commissioning || '');
      setValue('google_coordinates.latitude', d.google_coordinates?.latitude || '');
      setValue('google_coordinates.longitude', d.google_coordinates?.longitude || '');
      setValue('project_punch_point', d.project_punch_point || 'NILL');
      setValue('annexure_1', Array.isArray(d.annexure_1) ? d.annexure_1 : getValues('annexure_1'));
      setValue('acknowledgment', d.acknowledgment || 'Acknowledge and accept the handover of Electric Vehicle Charging Station (EVCS).');
      setValue('comments', d.comments || getValues('comments'));
      setValue('other_comments', d.other_comments || '');
      setValue('status', d.status || 'draft');
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue]);

  // ---------- Role/stage gating ----------
  const status = useWatch({ control, name: 'status' });
  const isEditableStage =
    role === 'admin' ||
    (role === 'engineer' && status === 'draft') ||
    (role === 'contractor' && status === 'CONTRACTOR') ||
    (role === 'manager' && status === 'MANAGER') ||
    (role === 'quality' && status === 'QUALITY');

  // ---------- Signatures ----------
  const handleImageUpload = (e, sigRole) => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        toast.error('File size exceeds 50KB. Please upload a smaller image.');
        handleResetSignature(sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => { setValue(`comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true }); };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (sigRole, type) => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    setValue(`comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setShowModal(true);
      setCurrentSignature({ role: sigRole });
    }
  };

  const handleResetSignature = (sigRole) => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`comments.${currentSignature.role}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  // ---------- Submit: single-level APFC flow ----------
  const onSubmit = (action) => (data) => {
    if (!isEditableStage && role !== 'admin') {
      toast.info('Form is not editable at this stage');
      return;
    }

    let nextStatus = data.status;

    if (role === 'engineer') {
      nextStatus = action === 'draft' ? 'draft' : 'CONTRACTOR';
    } else if (role === 'contractor') {
      nextStatus = action === 'draft' ? 'CONTRACTOR' : 'MANAGER';
    } else if (role === 'manager') {
      nextStatus = action === 'approved' ? 'QUALITY' : 'draft';
    } else if (role === 'quality') {
      nextStatus = action === 'approved' ? 'approved' : 'draft';
    } else if (role === 'admin') {
      // Admin can push as needed; default to pass-through
      if (action === 'approved') nextStatus = 'approved';
      else if (action === 'draft') nextStatus = 'draft';
      else nextStatus = 'MANAGER';
    }

    const payload = {
      form_data: {
        status: nextStatus,
        form_id: formId,
        formName: 'hoto',
        stationId: data.station_id,
        site_name: data.site_name,
        location_address: data.location_address,
        chargers: data.chargers.map((c) => c.name),
        charger_configuration: data.charger_configuration,
        internal_connection: data.internal_connection,
        new_evcs_connection: data.new_evcs_connection,
        sanctioned_load: data.new_evcs_connection === 'Yes' ? data.sanctioned_load : '',
        date_of_commissioning: data.date_of_commissioning,
        google_coordinates: data.google_coordinates,
        project_punch_point: data.project_punch_point,
        annexure_1: data.annexure_1,
        acknowledgment: data.acknowledgment,
        comments: data.comments,
        other_comments: data.other_comments,
      },
    };

    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    toast.success(`${action === 'approved' || action === 'pending' ? 'Submitted' : 'Saved as Draft'} Successfully`);
    navigate(-1);
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="page-body container-xl">
      <h2 className="page-title mb-4">PCS (SITE NAME) Handover Takeover Project</h2>

      {!isEditableStage && role !== 'admin' && (
        <div className="alert alert-warning mb-4">This form is currently view-only for your role/stage.</div>
      )}

      <Form>
        <h3>1. Site Details</h3>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Site Name</Form.Label>
              <Form.Control type="text" {...register('site_name')} disabled={!isEditableStage} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Location / Address</Form.Label>
              <Form.Control type="text" {...register('location_address')} disabled={!isEditableStage} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
          </Col>
        </Row>

        <h4>Chargers</h4>
        {chargerFields.map((field, index) => (
          <Row key={field.id} className="align-items-center mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Charger {index + 1}</Form.Label>
                <Form.Control type="text" {...register(`chargers.${index}.name`)} disabled={!isEditableStage} />
              </Form.Group>
            </Col>
            {index >= 2 && isEditableStage && (
              <Col md={1}>
                <Button variant="danger" size="sm" onClick={() => removeCharger(index)} disabled={!isEditableStage}>
                  -
                </Button>
              </Col>
            )}
          </Row>
        ))}
        {isEditableStage && (
          <Button variant="primary" size="sm" onClick={() => appendCharger({ name: '' })} className="mb-3">
            + Add Charger
          </Button>
        )}

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Charger Configuration</Form.Label>
              <Form.Control type="text" {...register('charger_configuration')} disabled={!isEditableStage} />
            </Form.Group>
          </Col>
        </Row>

        <h4>Power Connection</h4>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Internal Connection</Form.Label>
              <Form.Control type="text" {...register('internal_connection')} disabled={!isEditableStage} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>New EVCS Connection</Form.Label>
              <Form.Select {...register('new_evcs_connection')} disabled={!isEditableStage}>
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Form.Select>
            </Form.Group>
          </Col>
          {new_evcs_connection === 'Yes' && (
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Sanctioned Load</Form.Label>
                <Form.Control type="text" {...register('sanctioned_load')} disabled={!isEditableStage} />
              </Form.Group>
            </Col>
          )}
          <Col md={12}>
            <Form.Text className="text-muted">Note: If new EVCS connection, mention sanctioned load.</Form.Text>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Date of Commissioning</Form.Label>
              <Form.Control type="date" {...register('date_of_commissioning')} disabled={!isEditableStage} />
            </Form.Group>
          </Col>
        </Row>

        <h4>Google Coordinates</h4>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Latitude</Form.Label>
              <Form.Control type="text" {...register('google_coordinates.latitude')} disabled={!isEditableStage} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Longitude</Form.Label>
              <Form.Control type="text" {...register('google_coordinates.longitude')} disabled={!isEditableStage} />
            </Form.Group>
          </Col>
        </Row>

        <h3>2. Project Attachments</h3>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Project Documents (Annexure I)</Form.Label>
              <Form.Text className="text-muted">Attached Annexure – I</Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Project Images (Annexure II)</Form.Label>
              <Form.Text className="text-muted">Attached Annexure – II</Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Project Punch Point (Annexure III)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                {...register('project_punch_point')}
                disabled={!isEditableStage}
                placeholder="Enter project punch points or pending issues"
              />
              <Form.Text className="text-muted">Attached Annexure – III</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <h3>3. Annexure I - Item Details</h3>
        <Table bordered>
          <thead>
            <tr>
              <th>Item Description</th>
              <th>Serial Number / Login ID &amp; Password / Path</th>
              <th>Contact Person/Vendor Name</th>
              <th>Contact Details</th>
              <th>Documents Path</th>
            </tr>
          </thead>
          <tbody>
            {formValues.annexure_1.map((item, index) => (
              <tr key={index}>
                <td>{item.item_description}</td>
                <td><Form.Control type="text" {...register(`annexure_1.${index}.serial_number`)} disabled={!isEditableStage} /></td>
                <td><Form.Control type="text" {...register(`annexure_1.${index}.contact_person`)} disabled={!isEditableStage} /></td>
                <td><Form.Control type="text" {...register(`annexure_1.${index}.contact_details`)} disabled={!isEditableStage} /></td>
                <td><Form.Control type="text" {...register(`annexure_1.${index}.documents_path`)} disabled={!isEditableStage} /></td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h3>4. Acknowledgment</h3>
        <Form.Label>Acknowledge and accept the handover of Electric Vehicle Charging Station (EVCS).</Form.Label>

        <h3>5. Signatures</h3>
        <Table bordered>
          <thead>
            <tr>
              <th></th>
              <th>Issued By Project</th>
              <th>Acknowledged by Maintenance (M)</th>
              <th>Acknowledged by Operations (O)</th>
            </tr>
          </thead>
          <tbody>
            {['Name', 'Company', 'Signature', 'Date'].map((field) => (
              <tr key={field}>
                <td>{field}</td>
                {['project', 'maintenance', 'operations'].map((sigRole) => (
                  <td key={sigRole}>
                    {field === 'Date' ? (
                      <Form.Control type="date" {...register(`comments.${sigRole}.${field.toLowerCase()}`)} disabled={!isEditableStage} />
                    ) : field === 'Signature' ? (
                      <div>
                        <Form.Select
                          {...register(`comments.${sigRole}.signatureType`)}
                          onChange={(e) => handleSignatureTypeChange(sigRole, e.target.value)}
                          className="mb-2"
                          disabled={!isEditableStage}
                        >
                          <option value="upload">Image Upload</option>
                          <option value="digital">Digital Sign</option>
                        </Form.Select>
                        {_.get(formValues, `comments.${sigRole}.signatureType`) === 'upload' && (
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, sigRole)}
                            className="mb-2"
                            disabled={!isEditableStage}
                          />
                        )}
                        {_.get(formValues, `comments.${sigRole}.signature`) && (
                          <div>
                            <img
                              src={_.get(formValues, `comments.${sigRole}.signature`)}
                              alt="Signature"
                              style={{ maxWidth: '100px', marginTop: '5px' }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleResetSignature(sigRole)}
                              className="mt-2"
                              disabled={!isEditableStage}
                            >
                              Reset Signature
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Form.Control type="text" {...register(`comments.${sigRole}.${field.toLowerCase()}`)} disabled={!isEditableStage} />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>

        <h3>6. Other Comments/Remarks</h3>
        <Form.Group className="mb-3">
          <Form.Control
            as="textarea"
            rows={4}
            {...register('other_comments')}
            disabled={!isEditableStage}
            placeholder="Enter any additional comments or remarks"
          />
        </Form.Group>

        {/* Stage-specific actions */}
        {role === 'manager' && status === 'MANAGER' ? (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('approved'))} className="me-2">
              Approve (Send to QUALITY)
            </Button>
            <Button variant="danger" onClick={handleSubmit(onSubmit('draft'))}>
              Re-Work
            </Button>
          </div>
        ) : role === 'quality' && status === 'QUALITY' ? (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('approved'))} className="me-2">
              Final Approve
            </Button>
            <Button variant="danger" onClick={handleSubmit(onSubmit('draft'))}>
              Apply for Resubmit
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <Button
              variant="success"
              onClick={handleSubmit(onSubmit('pending'))}
              disabled={!isEditableStage}
            >
              Submit
            </Button>
            <Button
              variant="secondary"
              style={{ marginLeft: '10px' }}
              onClick={handleSubmit(onSubmit('draft'))}
              disabled={!isEditableStage}
            >
              Save As Draft
            </Button>
          </div>
        )}

        <Modal show={showModal && isEditableStage} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2" disabled={!isEditableStage}>
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature} disabled={!isEditableStage}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>
    </div>
  );
};

HOTOForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
};

export default HOTOForm;
